import { useEffect, useState } from "react";

import ChipMenu, { toggleValue, type MenuOption } from "@/shared/components/ChipMenu/ChipMenu";
import { engagementEvents } from "../../../../../engagementEvents.ts"
import { EngagementService } from "@services/engagements/engagementService.ts";
import { appStore } from "@store/appStore.ts";
import { Engagement } from "@models/engagement.model.ts";

export type StatusPill = "OPEN" | "CLOSED";
export type StatusFilter = Set<StatusPill>;

const PILL_CODES: Record<StatusPill, string[]> = {
  OPEN: ["STAGING", "SERVING", "PAUSED"],
  CLOSED: ["CLOSED"],
};

export function statusFilterCodes(filter: StatusFilter): string[] {
  const codes: string[] = [];
  for (const pill of filter) codes.push(...PILL_CODES[pill]);
  return codes;
}

export const TYPE_OPTIONS = ["REACH", "CREATE"] as const;

/**
 * The Sort by options, and the API key each sends.
 *
 * Single-select: exactly one is active at all times, and picking another re-sorts
 * immediately. Ordering is a server concern -- the list is paged, so re-ordering on screen
 * would sort the current page while looking like it sorted everything.
 */
export const SORT_OPTIONS = [
  { value: "-visited", label: "Recently Visited" },
  { value: "-updated", label: "Recently Updated" },
  { value: "-created", label: "Created" },
  { value: "name", label: "Name (A–Z)" },
  { value: "-live", label: "Status" },
] as const;

export const DEFAULT_SORT = "-visited";

/** Live / Not Live, matched against the derived count rather than a stored field. */
export const LIVE_OPTIONS = [
  { value: "live", label: "Live" },
  { value: "not", label: "Not Live" },
] as const;

/**
 * "Unassigned" in the people checklists.
 *
 * A named option rather than an employee id, because it means "no live role row of this
 * kind" and no id can express that. Kept as a string so it cannot collide with one.
 */
export const UNASSIGNED = "unassigned";

export interface PersonOption {
  employeeId: number;
  name: string;
}

interface Props {
  /** Employee ids holding Ops Lead. Multi-select, replacing the old single manager. */
  selectedManagerIds: number[];
  managerUnassigned: boolean;
  onManagerChange: (ids: number[], unassigned: boolean) => void;
  selectedCsmIds: number[];
  csmUnassigned: boolean;
  onCsmChange: (ids: number[], unassigned: boolean) => void;
  selectedStatus: StatusFilter;
  onStatusChange: (status: StatusFilter) => void;
  selectedTypes: string[];
  onTypesChange: (types: string[]) => void;
  /** Derived Live / Not Live. Both or neither ticked means no filter. */
  selectedLive: string[];
  onLiveChange: (values: string[]) => void;
  /** Ops Lead OR CSM is the signed-in user. */
  mine: boolean;
  onMineChange: (mine: boolean) => void;
  sort: string;
  onSortChange: (sort: string) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  /** Showing archived engagements instead of live ones (MPN-1533). */
  archived: boolean;
  onArchivedChange: (archived: boolean) => void;
  /** Super Admin. The toggle is theirs alone, matching who may archive. */
  canArchive: boolean;
  total: number;
}

function togglePill(current: StatusFilter, pill: StatusPill): StatusFilter {
  const next = new Set(current);
  if (next.has(pill)) {
    if (next.size > 1) next.delete(pill); // keep at least one active
  } else {
    next.add(pill);
  }
  return next;
}

/* `toggleValue` and `ChipMenu` moved to src/shared/components/ChipMenu when the Holdout
   report adopted the same filters (MPN-2045). Same component, same markup, same tests. */

function titleCase(code: string): string {
  return code.charAt(0) + code.slice(1).toLowerCase();
}

/** The selection a people checklist holds, as plain strings the menu can render. */
function peopleSelection(ids: number[], unassigned: boolean): string[] {
  return [...ids.map(String), ...(unassigned ? [UNASSIGNED] : [])];
}

export const EngagementListHeader = ({
  selectedManagerIds,
  managerUnassigned,
  onManagerChange,
  selectedCsmIds,
  csmUnassigned,
  onCsmChange,
  selectedStatus,
  onStatusChange,
  selectedTypes,
  onTypesChange,
  selectedLive,
  onLiveChange,
  mine,
  onMineChange,
  sort,
  onSortChange,
  searchTerm,
  onSearchChange,
  archived,
  onArchivedChange,
  canArchive,
  total,
}: Props) => {
  const [opsLeads, setOpsLeads] = useState<PersonOption[]>([])
  const [csms, setCsms] = useState<PersonOption[]>([])
  const [searchInput, setSearchInput] = useState<string>(searchTerm)

  // Keep local input in sync if URL param changes externally (e.g., back button)
  useEffect(() => {
    setSearchInput(searchTerm)
  }, [searchTerm])

  // Debounce commit to parent
  useEffect(() => {
    if (searchInput === searchTerm) return
    const t = setTimeout(() => onSearchChange(searchInput), 300)
    return () => clearTimeout(t)
  }, [searchInput, searchTerm, onSearchChange])

  function handleNewEngagementClicked() {
    engagementEvents.send_event("NEW_ENGAGEMENT_CLICKED")
  }

  /*
      Both people lists come from ONE fetch.

      There is still no endpoint that lists who holds a role, so this reads a wide page of
      engagements and collects the names off them -- the shape that was already here for the
      manager filter. Adding the CSM list to the same pass rather than repeating the fetch is
      the whole reason this is one effect: a second 500-row request to build a second
      dropdown would double the cost of opening the page for a list that is a dozen names
      long. A `/engagements/team-options/` endpoint is the honest fix when someone has a
      moment.
  */
  useEffect(() => {
    const loadPeople = async () => {
      try {
        const service = new EngagementService()
        const response: Engagement[] = await service.getEngagementsList(
          appStore.loggedInUser.organization.id,
          500,
          0,
          { status: statusFilterCodes(selectedStatus) },
        )
        const collect = (pick: (e: Engagement) => Engagement["engagement_manager"] | null) => {
          const byId = new Map<number, PersonOption>()
          for (const engagement of response) {
            const employee = pick(engagement)?.employee
            const person = employee?.person
            if (employee?.id && person?.first_name && !byId.has(employee.id)) {
              byId.set(employee.id, {
                employeeId: employee.id,
                name: `${person.first_name ?? ""} ${person.last_name ?? ""}`.trim(),
              })
            }
          }
          return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name))
        }
        setOpsLeads(collect((e) => e.engagement_manager))
        setCsms(collect((e) => e.client_success_manager))
      } catch (error) {
        console.error("Error loading engagement team options:", error)
      }
    }
    loadPeople()

  }, [appStore.loggedInUser.organization.id, ...selectedStatus])

  const peopleOptions = (people: PersonOption[]): MenuOption[] => [
    ...people.map((person) => ({ value: String(person.employeeId), label: person.name })),
    { value: UNASSIGNED, label: "Unassigned" },
  ]

  const onPersonToggle = (
    ids: number[], unassigned: boolean, change: (ids: number[], unassigned: boolean) => void,
  ) => (value: string) => {
    if (value === UNASSIGNED) return change(ids, !unassigned)
    const id = Number(value)
    return change(ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id], unassigned)
  }

  return (
    <div className="eng-bar">
      <span className="eng-bar__title">Engagements</span>
      <span className="eng-bar__count">{total}</span>

      <label className="eng-search">
        <i className="bi bi-search" />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search name or customer…"
          aria-label="Search engagements"
        />
        {searchInput && (
          <button type="button" onClick={() => setSearchInput("")} aria-label="Clear search">
            <i className="bi bi-x" />
          </button>
        )}
      </label>

      <div className="eng-seg">
        {(["OPEN", "CLOSED"] as StatusPill[]).map((pill) => (
          <button
            key={pill}
            type="button"
            className={`eng-seg__btn${selectedStatus.has(pill) ? " eng-seg__btn--on" : ""}`}
            onClick={() => onStatusChange(togglePill(selectedStatus, pill))}
          >
            {titleCase(pill)}
          </button>
        ))}
      </div>

      {/*
          One click for "my book of business". Beside Open/Closed and sharing their active
          fill, because it narrows who the list is about in the same way they narrow what
          state it is in -- and because assembling it out of the Ops Lead dropdown was
          impossible for a CSM, which is the gap it closes.
      */}
      <button
        type="button"
        className={`eng-chip eng-chip--toggle${mine ? " eng-chip--mine" : ""}`}
        onClick={() => onMineChange(!mine)}
        aria-pressed={mine}
      >
        My Engagements
      </button>

      <ChipMenu
        label="Type"
        options={TYPE_OPTIONS.map((t) => ({ value: t, label: titleCase(t) }))}
        selected={selectedTypes}
        onToggle={(v) => onTypesChange(toggleValue(selectedTypes, v))}
        onClear={() => onTypesChange([])}
      />

      <ChipMenu
        label="Status"
        options={LIVE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        selected={selectedLive}
        onToggle={(v) => onLiveChange(toggleValue(selectedLive, v))}
        onClear={() => onLiveChange([])}
      />

      {/*
          "Ops Lead", not "Manager". A label change only: the stored role is still
          ENGAGEMENT_MANAGER, which review-todo routing reads and twenty-odd live rows carry.
      */}
      <ChipMenu
        label="Ops Lead"
        options={peopleOptions(opsLeads)}
        selected={peopleSelection(selectedManagerIds, managerUnassigned)}
        onToggle={onPersonToggle(selectedManagerIds, managerUnassigned, onManagerChange)}
        onClear={() => onManagerChange([], false)}
      />

      <ChipMenu
        label="CSM"
        options={peopleOptions(csms)}
        selected={peopleSelection(selectedCsmIds, csmUnassigned)}
        onToggle={onPersonToggle(selectedCsmIds, csmUnassigned, onCsmChange)}
        onClear={() => onCsmChange([], false)}
      />

      <ChipMenu
        label="Sort by"
        single
        options={SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        selected={[sort]}
        onToggle={onSortChange}
      />

      {/*
          Archived is a mode, not another status pill.

          It sits apart from the status filters on purpose: those narrow a set of live
          engagements, while this swaps which set you are looking at. Putting it in the
          same row would suggest you could combine SERVING with Archived and get the
          intersection, which is not what the server does.

          Shown only to Super Admin, matching who can archive. Everyone else has no way
          to reach an archived engagement and no reason to know the mode exists.
      */}
      {canArchive && (
        <button
          type="button"
          className={`btn btn-sm ${archived ? "btn-secondary" : "btn-outline-secondary"}`}
          style={{ fontSize: "0.74rem" }}
          onClick={() => onArchivedChange(!archived)}
          aria-pressed={archived}
        >
          <i className="bi bi-archive me-1" />
          {archived ? "Viewing archived" : "Archived"}
        </button>
      )}

      <button
        type="button"
        className={`btn btn-primary btn-sm ${canArchive ? "" : "ms-auto"}`}
        style={{ fontSize: "0.74rem" }}
        onClick={handleNewEngagementClicked}
      >
        <i className="bi bi-plus-lg me-1" />
        Engagement
      </button>
    </div>
  )
}
